/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph, {ERenderFiler} from "App/render/webgl/render_graph";
import View from "App/view/view";
import RenderObject from "App/geometry/objects/object";
import WebGLContext from "App/web_gl_context";

import PrimitiveID from "App/render/webgl/shaders/primitive_id/primitive_id";
import LinePrimitiveID from "App/render/webgl/shaders/primitive_id/line_primitive_id";
import PointPrimitiveID from "App/render/webgl/shaders/primitive_id/point_primitive_id";

import Factory from "App/render/webgl/passes/factory";
import {RenderOptionNodeTypeName} from "App/render/webgl/nodes/type_names";
import {
    AlphaTransparencyBinTypeName,
    LinesBinTypeName,
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName,
    OutlinesBinTypeName,
    PoiBinTypeName, PointsBinTypeName,
    TextBinTypeName,
    TransparentBinTypeName, TransparentInternalBinTypeName,
    TransparentNoTextureBinTypeName, TransparentNoTextureInternalBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import {PrimitiveIdPassTypeName} from "App/render/webgl/passes/type_names";
import IScene from "js_interface/src/scene_interface";

export default 
class PrimitiveId extends Pass {

    public static TypeName: string = PrimitiveIdPassTypeName;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): PrimitiveId {
        return new PrimitiveId(aSceneAPI, aRenderGraph);
    }

    public initialize() {
        let shaders = this._renderGraph.getShaders();

        this._shadersToBins = {};

        this.attachShaderToBin(OpaqueBinTypeName, shaders.get(PrimitiveID.TypeName));
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, shaders.get(PrimitiveID.TypeName));
        this.attachShaderToBin(AlphaTransparencyBinTypeName, shaders.get(PrimitiveID.TypeName));
        this.attachShaderToBin(TextBinTypeName, shaders.get(PrimitiveID.TypeName));
        this.attachShaderToBin(OutlinesBinTypeName, shaders.get(PrimitiveID.TypeName));
        this.attachShaderToBin(LinesBinTypeName, shaders.get(LinePrimitiveID.TypeName));
        this.attachShaderToBin(PoiBinTypeName, shaders.get(PointPrimitiveID.TypeName));
        this.attachShaderToBin(TransparentBinTypeName, shaders.get(PrimitiveID.TypeName));
        this.attachShaderToBin(TransparentNoTextureBinTypeName, shaders.get(PrimitiveID.TypeName));
        this.attachShaderToBin(TransparentInternalBinTypeName, shaders.get(PrimitiveID.TypeName));
        this.attachShaderToBin(TransparentNoTextureInternalBinTypeName, shaders.get(PrimitiveID.TypeName));
        this.attachShaderToBin(PointsBinTypeName, shaders.get(PointPrimitiveID.TypeName));
    }

    public readPrimitiveIdValue(aView, aProperties): any {
        let framebuffer = this._renderGraph.getFramebuffers().primitiveID;

        framebuffer.bind();

        let ret = framebuffer.readPrimitiveIdValue(
            aView,
            aProperties.readPosition[0],
            aProperties.readPosition[1]);

        framebuffer.unbind();

        return ret;
    }

    /**
     * Render only the geometry objects with id specified in the render function a reference to the shape object
     * founded is stored in the render pass instance for later usage.
     *
     * @param {View} aView
     * @param {int} geometryObjectUID geometry object with UID which will be rendered alone
     */
    public render(aView: View, aGeometryObject: RenderObject) {
        let framebuffer = this._renderGraph.getFramebuffers().primitiveID;

        let scale = framebuffer.parameters.scale;
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        // set rendering target
        framebuffer.bind();

        let position = aView.getPosition();
        let size = aView.getSize();

        // set viewport
        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.scissor(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);

        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        // render all the bins related to the primitive id pass
        let bins = this._renderGraph.getRenderBins();

        this.view = aView;

        this.state.setRenderNodesFilters([{
            type: RenderOptionNodeTypeName,
            value: ERenderFiler.pickable
        }
        ]);

        this.state.setRenderSingleGeometryObject(aGeometryObject);

        bins.get(OpaqueBinTypeName).render(this);
        bins.get(OpaqueNoTextureBinTypeName).render(this);
        bins.get(AlphaTransparencyBinTypeName).render(this);
        bins.get(TextBinTypeName).render(this);
        bins.get(OutlinesBinTypeName).render(this);
        bins.get(LinesBinTypeName).render(this);
        bins.get(PoiBinTypeName).render(this);
        bins.get(TransparentBinTypeName).render(this);
        bins.get(TransparentNoTextureBinTypeName).render(this);
        bins.get(PointsBinTypeName).render(this);

        this.state.setRenderSingleGeometryObject(null); // reset
        this.state.resetRenderNodesFilters();

        gl.disable(gl.SCISSOR_TEST);

        framebuffer.unbind(this._typeName);

        this.view = null;
    }
}

Factory.register(PrimitiveId);
